import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTriggerButton,
  useModals,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { getLabelForItem } from '../utils/item';
import { EditDetailsModal } from './edit-details-modal';
import { useItemWrites, type ItemWriteTarget } from './item-writes';

/**
 * What can be done to one attribute from its own page.
 *
 * Only an attribute has a menu: an event cannot be described into something
 * else, redacted, or deleted. Every reason an item cannot be deleted comes
 * from the same gate the list uses, so the two never disagree.
 */

import { SENSITIVE_PRIVACY_LEVEL } from '../utils/types';

const NO_EDIT_TOOLTIP = "You don't have permission to edit this attribute";
const NO_SENSITIVITY_TOOLTIP = "You don't have permission to mark this attribute as sensitive";

/** The privacy level the backend uses for an attribute someone marked sensitive. */

export type ItemActionsProps = {
  target: ItemWriteTarget;
  /** Whether the plan and the attribute allow marking it sensitive at all. */
  canBeMarkedSensitive: boolean;
  /** Why the attribute cannot be deleted, or nothing when it can. */
  deleteBlockReason: string | undefined;
};

export function ItemActions({ target, canBeMarkedSensitive, deleteBlockReason }: ItemActionsProps) {
  const { hasPermission } = usePermissions();
  const modals = useModals();
  const writes = useItemWrites(target);

  // Tags are attribute metadata, and the list gates them the same way.
  const canEdit = hasPermission('journeys/attribute_metadata/edit');
  const isSensitive = target.privacyLevel === SENSITIVE_PRIVACY_LEVEL;

  const openEdit = () => {
    modals.open(
      EditDetailsModal,
      {
        workspaceId: target.workspaceId,
        name: target.name,
        description: target.description,
        tags: target.tags,
        type: getLabelForItem(target.kind),
        objectTypeId: target.scope.kind === 'profile' ? undefined : target.scope.objectTypeId,
        canEditTags: canEdit,
        onSave: writes.saveDetails,
      },
      { shouldCloseOnUnmount: true },
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTriggerButton variant="primary" icon="chevron-down" iconPosition="trailing">
        Actions
      </DropdownMenuTriggerButton>
      <DropdownMenuContent>
        <DropdownMenuItem
          icon="edit"
          isDisabled={!canEdit}
          tooltip={canEdit ? undefined : NO_EDIT_TOOLTIP}
          onClick={openEdit}
        >
          Edit details
        </DropdownMenuItem>
        {canBeMarkedSensitive ? (
          <SensitivityAction
            isSensitive={isSensitive}
            isDisabled={!canEdit || writes.isChangingSensitivity}
            onClick={() => void writes.toggleSensitivity()}
          />
        ) : null}
        <DropdownMenuItem
          icon="delete-trash"
          isCritical
          isDisabled={deleteBlockReason != null}
          tooltip={deleteBlockReason}
          onClick={() => void writes.confirmDelete()}
        >
          Delete attribute
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type SensitivityActionProps = {
  isSensitive: boolean;
  isDisabled: boolean;
  onClick: () => void;
};

/** Redacting the attribute from teammates, or putting it back. */
function SensitivityAction({ isSensitive, isDisabled, onClick }: SensitivityActionProps) {
  return (
    <DropdownMenuItem
      icon={isSensitive ? 'view-off' : 'view-on'}
      isDisabled={isDisabled}
      tooltip={isDisabled ? NO_SENSITIVITY_TOOLTIP : undefined}
      onClick={onClick}
    >
      {isSensitive ? 'Make insensitive' : 'Make sensitive'}
    </DropdownMenuItem>
  );
}
